import {
  ITEMS,
  inGrid,
  isContainer,
  atHelmOrTurret,
  isDoor,
  isHelm,
  isHatch,
  isMachine,
  isPartOf,
  isSolid,
  roomFor,
  T,
  CHARACTER,
  checkEdit,
  count,
  digNeeds,
  digTimeAt,
  dropFor,
  editTargetAt,
  editTargetCenter,
  editTargetTile,
  itemForTile,
  cellTile,
  packCell,
  pieceAnchor,
  pieceBox,
  pieceOf,
  placesAs,
  plantAtTarget,
  placingWays,
  playerWorldBox,
  playerWorldCenter,
  playerWorldUp,
  steamEngineFacing,
  steamExhaust,
  tileNear,
  toolOf,
  HOTBAR_SLOTS,
  emptySlots,
  freeSlot,
  moveSlot,
  onHotbar,
  turnAt,
  v2,
  vDist,
  worldToShip,
  type AsteroidDef,
  type ClientMessage,
  type EditTarget,
  type Gear,
  type Inventory,
  type ItemId,
  type PlacingWay,
  type PlayerState,
  type Slots,
  type Sim,
  type TileId,
  type Vec2,
  type Wear,
} from "@dyson/shared";

/** What Building needs from the game around it. */
export interface BuildContext {
  /** The world as we predict it (our flown ship where we think it is). */
  sim(): Sim | undefined;
  /** Our own predicted character. */
  me(): PlayerState | undefined;
  /** Everyone else, for "someone is standing there". */
  players(): Iterable<PlayerState>;
  /** The mouse, in world coordinates as the sim has them (over a drifting rock, the spot on it that's drawn under the mouse). */
  mouse(): Vec2;
  /** Where a rock's grid origin is drawn (a drifting one lags its sim place a little): its cursor goes there. */
  drawnOrigin(def: AsteroidDef): Vec2;
  leftDown(): boolean;
  rightDown(): boolean;
  send(msg: ClientMessage): void;
  /** Take the helm or the turret seat we stand at, or leave it (see PlayerInput.interact). */
  interact(): void;
  /** Throw our grapple, or let go of its line (see PlayerInput.action). */
  grapple(): void;
  /** Our character swings once (we just placed something). */
  swing(): void;
  /** A line in the status bar for a few seconds. */
  say(text: string): void;
  /** Right click on a chest: look inside. */
  openChest(target: EditTarget): void;
  /** Right click on a sign: write on it. */
  editSign(target: EditTarget): void;
  /** Asteroid tiles on fire, as "asteroidId:index". */
  groundFires(): Set<string>;
}

/** Does an item place a steam engine (which can face away from the ship's middle by itself)? */
const placesEngine = (item: ItemId): boolean => {
  const t = placesAs(item);
  return t !== undefined && steamExhaust(t) !== null;
};

/**
 * The ways round an item goes down, which T steps through: its blocks (see
 * placingWays). A steam engine starts on "auto" (null: facing away from the
 * ship's middle), then each fixed way its exhaust can point. Undefined for an
 * item that only goes one way.
 */
function waysOf(item: ItemId): readonly (PlacingWay | null)[] | undefined {
  const ways = placingWays(item);
  if (ways.length < 2) return undefined;
  return placesEngine(item) ? [null, ...ways] : ways;
}

/**
 * The build cursor: the hovered tile's corner (grid-local when `shipId` is
 * set), whether it's allowed, and dig progress. It covers a whole piece when
 * placing or digging one.
 */
export interface Cursor {
  shipId?: string;
  x: number;
  y: number;
  /** Blocks across and up. */
  w: number;
  h: number;
  ok: boolean;
  progress: number;
  /** What right click would put there (a cell: the tile and its shape, see packCell; an anchor, for a piece), shown faintly in place. */
  ghost?: number;
  /** Quarter turns the ghost (a piece, or a shape) is turned by, as the grid turns there (see turnAt). */
  turn?: number;
}

interface Hover {
  target: EditTarget;
  /** Why placing `tile` (or digging, for EMPTY) there would be refused, or null if it wouldn't. */
  verdict: (tile: number) => string | null;
}

/**
 * Everything you do with your hands: the quick-access bar and what's in it,
 * digging (hold left button), placing, feeding furnaces and using hatches
 * and chests (right button),
 * your inventory and how worn your tools are, and the build cursor.
 */
export class Building {
  /** What we carry, how worn our tools are and what we wear, as the server last told us. */
  inventory: Inventory = {};
  wear: Wear = {};
  gear: Gear = {};
  /** Where it all sits in the pack, as the server last told us (or as we've just moved it): the first 9 are the hotbar. */
  slots: Slots = emptySlots();
  private hand = 0;
  /** Last tile placed while the right button is held, so dragging places on each tile once. */
  private lastPaint = "";
  /** The block being dug out with the left button held, and how long we've been at it (s). */
  private mining: { key: string; time: number; progress: number; target: EditTarget } | null = null;
  /** Which way round each item goes down (an index into ROTATIONS). */
  private readonly rotation: Partial<Record<ItemId, number>> = {};

  constructor(private readonly ctx: BuildContext) {}

  /** T: turn what the selected slot places to its next way round. Returns what it is now, for the status line, or null if it doesn't turn. */
  rotate(): string | null {
    const item = this.quickbar[this.selected];
    const ways = item && waysOf(item);
    if (!item || !ways) return null;
    const r = ((this.rotation[item] ?? 0) + 1) % ways.length;
    this.rotation[item] = r;
    return `${ITEMS[item].name}: ${ways[r]?.way ?? "auto"} (T to turn)`;
  }

  /** The hotbar slot in your hand (keys 1-9). The server's told, so what you use comes out of this slot first. */
  get selected(): number {
    return this.hand;
  }

  set selected(i: number) {
    if (i === this.hand) return;
    this.hand = i;
    this.ctx.send({ t: "select", slot: i });
  }

  /** Tell the server which slot is in hand (a fresh connection starts it at the first). */
  sendSelected(): void {
    this.ctx.send({ t: "select", slot: this.hand });
  }

  /** What's in each hotbar slot (keys 1-9). */
  get quickbar(): (ItemId | null)[] {
    return this.slots.slice(0, HOTBAR_SLOTS).map((s) => s?.item ?? null);
  }

  /** Move `n` from pack slot `from` to `to` (see moveSlot): done here at once, and the server's told (it sends the pack back either way). */
  move(from: number, to: number, n = this.slots[from]?.n ?? 0): boolean {
    const next = this.slots.map((s) => (s ? { ...s } : null));
    if (!moveSlot(next, from, to, n)) return false;
    this.slots = next;
    this.ctx.send({ t: "slot", from, to, n });
    return true;
  }

  /**
   * Put a stack of `item` in hotbar slot `slot` (what was there goes where it
   * came from), or with null, put what's in the slot away in the grid.
   */
  assign(slot: number, item: ItemId | null): void {
    if (!item) {
      const free = freeSlot(this.slots);
      if (free !== null && !onHotbar(free)) this.move(slot, free);
      return;
    }
    if (this.slots[slot]?.item === item) return;
    // The biggest stack of it in the grid, else one elsewhere on the hotbar.
    const from = this.slots
      .map((s, i) => ({ s, i }))
      .filter(({ s, i }) => s?.item === item && i !== slot)
      .sort((a, b) => Number(onHotbar(a.i)) - Number(onHotbar(b.i)) || b.s!.n - a.s!.n)[0];
    if (from) this.move(from.i, slot);
  }

  swap(a: number, b: number): void {
    this.move(a, b);
  }

  /** A new inventory from the server. */
  takeInventory(items: Inventory, wear: Wear, gear: Gear, slots: Slots): void {
    this.inventory = items;
    this.wear = wear;
    this.gear = gear;
    this.slots = slots;
  }

  /** The items on the hotbar, which the old inventory panel (I) leaves out. */
  get onBar(): Set<ItemId> {
    return new Set(this.quickbar.filter((x): x is ItemId => !!x));
  }

  /** What's in your hand: the selected hotbar slot's item, if there's any of it. */
  heldItem(): ItemId | null {
    return this.slots[this.selected]?.item ?? null;
  }

  /**
   * The cell (the tile and its shape, see packCell) the selected slot would
   * place at `target`. A steam engine faces away from the middle of the ship
   * it's built on, so one put on the bottom blows downward (and lifts), one at
   * the stern blows backward, and so on.
   */
  private selectedTile(target?: EditTarget): number | undefined {
    const item = this.quickbar[this.selected];
    if (!item) return undefined;
    const turned = waysOf(item)?.[this.rotation[item] ?? 0];
    if (turned) return packCell(turned.tile, turned.shape ?? 0);
    const ship = placesEngine(item) && target?.kind === "ship" ? this.ctx.sim()?.ships.get(target.id) : undefined;
    const b = ship?.stats.bounds;
    if (!b) return placesAs(item);
    return steamEngineFacing(placesAs(item)!, target!.x + 0.5 - (b.x0 + b.x1) / 2, target!.y + 0.5 - (b.y0 + b.y1) / 2);
  }

  /**
   * Right button pressed or dragged: take or leave the helm or a turret, open
   * or shut a hatch, look in a chest, feed a furnace, and the like (all within
   * CHARACTER.useReach), or else place the selected block on the hovered tile.
   * Holding the grapple, anything else throws it (or lets go of its line).
   */
  paint(): void {
    const me = this.ctx.me();
    const sim = this.ctx.sim();
    if (!this.ctx.rightDown() || !me || !sim) {
      this.lastPaint = "";
      return;
    }
    // Using something happens once per press, however the mouse moves.
    const use = (what: string, act: () => void) => {
      if (this.lastPaint !== what) act();
      this.lastPaint = what;
    };
    // At the helm or in a turret, right click gets up; and the press that took or left it does nothing else till it's let go.
    if (me.piloting || me.gunning !== null) return use("helm", () => this.ctx.interact());
    if (this.lastPaint === "helm") return;
    const held = this.heldItem();
    const grapple = () => use("grapple", () => this.ctx.grapple());
    const hover = this.hover();
    // Out past build reach is where the grapple goes most.
    if (!hover) return held === "grapple" ? grapple() : undefined;
    const t = hover.target;
    const there = editTargetTile(sim, t);
    // A patch kit on a cracked block mends it.
    if (held === "patchKit" && damagedAt(sim, t)) return use("repair", () => this.ctx.send({ t: "repair", target: t }));
    // Water on a fire (on it, or right beside it) puts it out.
    if (held === "water" && fireNear(sim, t, this.ctx.groundFires())) return use("douse", () => this.ctx.send({ t: "douse", target: t }));
    // The helm or turret seat you're standing at (see atHelmOrTurret): take it. From anywhere else, say where to stand.
    if (isHelm(there) || isTurretPart(there)) {
      if (atHelmOrTurret(me, sim)) return use("helm", () => this.ctx.interact());
      if (held === "grapple") return grapple();
      return use("station", () => this.ctx.say(stationHint(there)));
    }
    if (usable(there)) {
      const centre = editTargetCenter(sim, t);
      if (!centre || vDist(centre, playerWorldCenter(me, sim)) > CHARACTER.useReach) {
        if (held === "grapple") return grapple();
        return use("far", () => this.ctx.say("Get closer to use it"));
      }
      if (isHatch(there)) return use("hatch", () => this.ctx.send({ t: "hatch", target: t }));
      if (isContainer(there)) return use("chest", () => this.ctx.openChest(t));
      if (there === T.SIGN) return use("sign", () => this.ctx.editSign(t));
      if (isPartOf(there, T.BED)) return use("bed", () => this.ctx.send({ t: "bed", target: t }));
      if (isMachine(there, "winch")) return use("winch", () => this.ctx.send({ t: "winch", target: t }));
      // Holding fuel or water feeds just that; anything else, the best fuel you have and a water.
      const feed = held && (ITEMS[held].fuel || ITEMS[held].water) ? held : undefined;
      return use("feed", () => this.ctx.send({ t: "stoke", item: feed }));
    }
    if (held === "grapple") return grapple();
    const tile = this.selectedTile(t);
    if (tile === undefined) return;
    const key = `${t.kind}:${t.id}:${t.x}:${t.y}:${tile}`;
    if (key === this.lastPaint) return;
    this.lastPaint = key;
    if (hover.verdict(tile) === null) {
      this.ctx.send({ t: "edit", target: t, tile });
      this.ctx.swing();
    }
  }

  /**
   * Every frame: dig if the left button is held, and say where the cursor
   * goes and what (if anything) to tell the player about the hovered tile.
   */
  update(dt: number): { cursor?: Cursor; note: string; sign?: EditTarget } {
    const me = this.ctx.me();
    const hover = me && !me.piloting ? this.hover() : null;
    const progress = this.mine(dt, hover);
    const sim = this.ctx.sim();
    if (!hover || !sim) return { note: "" };
    const t = hover.target;
    const tile = this.selectedTile(t);
    const place = tile === undefined ? "nothing to place" : hover.verdict(tile);
    const dig = hover.verdict(T.EMPTY);
    const ok = !place || !dig;
    // Refused for a reason worth showing (not just "too far" or "nothing there").
    const quiet = ["nothing to place", "out of reach", "nothing there", "nothing to remove", "too far to dig", "occupied"];
    const reason = !ok ? [dig, place].find((r) => r && !quiet.includes(r)) : undefined;
    // Hatches, doors, chests, beds and the like are used (right click) from close by; mending, from anywhere in build reach.
    const there = editTargetTile(sim, t);
    const centre = editTargetCenter(sim, t);
    const away = centre ? vDist(centre, playerWorldCenter(me!, sim)) : Infinity;
    const hint = away <= CHARACTER.reach ? useHint(there, this.heldItem(), damagedAt(sim, t)) : "";
    const station = (isHelm(there) || isTurretPart(there)) && away <= CHARACTER.reach && !atHelmOrTurret(me!, sim) ? stationHint(there) : "";
    const use = station || (hint && usable(there) && away > CHARACTER.useReach ? "Get closer to use it" : hint);
    const sign = there === T.SIGN && t.kind !== "space" ? t : undefined;
    if (!ok && !reason) return { note: use, sign };
    // Ship tiles are in the ship's turned grid; asteroid and open-space tiles are square to the world.
    const asteroid = t.kind === "asteroid" ? sim.asteroids.find((a) => a.def.id === t.id) : undefined;
    const grid = t.kind === "ship" ? sim.ships.get(t.id)?.grid : asteroid?.grid;
    const cursor: Cursor = { x: t.x, y: t.y, w: 1, h: 1, ok, progress };
    // A piece comes out whole (outline all of it); placing shows what would go where, a piece all of it.
    const anchor = grid && place && !dig ? pieceAnchor(grid, t.y * grid.w + t.x) : null;
    if (grid && anchor !== null) {
      // The whole piece, however it's turned (see turnAt): the box of tiles it covers.
      const ax = anchor % grid.w;
      const ay = Math.floor(anchor / grid.w);
      const box = pieceBox(pieceOf(grid.tiles[anchor]!)!.def, turnAt(grid, ax, ay));
      Object.assign(cursor, { x: ax + box.x0, y: ay + box.y0, w: box.w, h: box.h });
    } else if (tile !== undefined && (!place || dig)) {
      const def = pieceOf(cellTile(tile))?.def;
      const turn = grid ? turnAt(grid, t.x, t.y) : 0;
      const box = def ? pieceBox(def, turn) : { x0: 0, y0: 0, w: 1, h: 1 };
      Object.assign(cursor, { x: t.x + box.x0, y: t.y + box.y0, w: box.w, h: box.h, ghost: tile, turn });
    }
    if (t.kind === "ship") cursor.shipId = t.id;
    else if (asteroid) {
      const o = this.ctx.drawnOrigin(asteroid.def);
      [cursor.x, cursor.y] = [o.x + cursor.x, o.y + cursor.y];
    }
    return { cursor, note: reason ?? use, sign };
  }

  /**
   * Left button held on foot: swing at the block under the cursor until it's
   * been hit for its dig time, then break it. Moving off it, switching tool or
   * letting go starts over. Returns how far through the current block we are, 0..1.
   */
  private mine(dt: number, hover: Hover | null): number {
    const me = this.ctx.me();
    const sim = this.ctx.sim();
    if (!me || !sim || me.piloting || !this.ctx.leftDown() || !hover || hover.verdict(T.EMPTY) !== null) {
      this.mining = null;
      return 0;
    }
    const t = hover.target;
    const held = this.heldItem();
    // Any part of a plant brings the whole plant down, so swinging across its tiles keeps at the same one.
    const plant = plantAtTarget(sim, t);
    const key = plant ? `plant:${t.id}:${plant.plant}:${held}` : `${t.kind}:${t.id}:${t.x}:${t.y}:${held}`;
    const time = digTimeAt(sim, t, toolOf(held)) ?? Infinity;
    if (this.mining?.key !== key) this.mining = { key, time, progress: 0, target: t };
    this.mining.target = t;
    this.mining.progress += dt;
    if (this.mining.progress < this.mining.time) return this.mining.progress / this.mining.time;
    this.ctx.send({ t: "edit", target: t, tile: T.EMPTY });
    this.mining = null;
    return 1;
  }

  /** The tile under the mouse and whether an edit there would be accepted. */
  private hover(): Hover | null {
    const me = this.ctx.me();
    const sim = this.ctx.sim();
    if (!sim || !me) return null;
    const w = this.ctx.mouse();
    // Still swinging at a block: it stays the target till the cursor's well onto another, so a drifting rock doesn't start the dig over.
    const swingingAt = this.mining && this.ctx.leftDown() ? this.mining.target : null;
    const target = swingingAt && nearlyOver(sim, swingingAt, w) ? swingingAt : editTargetAt(sim, w.x, w.y, me.frame);
    const reachFrom = playerWorldCenter(me, sim);
    const occupied = [...this.ctx.players()].map((p) => playerWorldBox(p.id === me.id ? me : p, sim));
    const verdict = (tile: number) => {
      const item = tile === T.EMPTY ? undefined : itemForTile(cellTile(tile));
      if (item && count(this.inventory, item) < 1) return `no ${ITEMS[item].name.toLowerCase()} left: dig or craft some (I)`;
      const problem = checkEdit(sim, target, tile, reachFrom, occupied, playerWorldUp(me, sim));
      if (problem || tile !== T.EMPTY) return problem;
      // Some blocks only come out with the right tool (or a good enough one) in hand.
      const there = editTargetTile(sim, target);
      const needs = digNeeds(there, toolOf(this.heldItem()));
      if (needs) return needs;
      const drop = dropFor(there);
      return drop && !roomFor(this.inventory, { [drop]: 1 }) ? "your pack is full" : null;
    };
    return { target, verdict };
  }
}

/** How far (blocks) the cursor can stray off the block being dug before it moves on to the next. */
const DIG_SLIP = 0.35;

/** Is world point `w` over the block at `t`, or within DIG_SLIP of it, and the block still there? */
function nearlyOver(sim: Sim, t: EditTarget, w: Vec2): boolean {
  if (t.kind === "space" || editTargetTile(sim, t) === T.EMPTY) return false;
  const ship = t.kind === "ship" ? sim.ships.get(t.id) : undefined;
  const a = t.kind === "asteroid" ? sim.asteroids.find((x) => x.def.id === t.id) : undefined;
  const local = ship ? worldToShip(ship, w) : a ? v2(w.x - a.def.ox, w.y - a.def.oy) : null;
  return !!local && local.x > t.x - DIG_SLIP && local.x < t.x + 1 + DIG_SLIP && local.y > t.y - DIG_SLIP && local.y < t.y + 1 + DIG_SLIP;
}

/** What right click (or E) would do with tile `t` in reach, for the hint line; "" if nothing. */
/** A turret's or harpoon's piece, which right click mans from its seat. */
const isTurretPart = (t: TileId): boolean => isMachine(t, "turret") || isMachine(t, "harpoon");

/** Right click uses it (from within CHARACTER.useReach) rather than building on it. */
function usable(t: TileId): boolean {
  return isHelm(t) || isTurretPart(t) || isHatch(t) || isContainer(t) || t === T.SIGN || isPartOf(t, T.BED) || isMachine(t, "winch") || isMachine(t, "furnace");
}

/** Where to stand to take the helm or man a turret or harpoon (`t` is part of it). */
function stationHint(t: TileId): string {
  if (isHelm(t)) return "Stand at the helm to take it";
  return `Climb into the ${isMachine(t, "harpoon") ? "harpoon" : "turret"}'s seat to man it`;
}

function useHint(t: TileId, held: ItemId | null, damaged: boolean): string {
  if (isHatch(t)) return `Right click to ${isSolid(t) ? "open" : "shut"} the ${isDoor(t) ? "door" : "hatch"}`;
  if (isPartOf(t, T.CRATE)) return "Right click to open the crate";
  if (isContainer(t)) return "Right click to open the chest";
  if (isPartOf(t, T.BED)) return "Right click to sleep here: you'll wake up here when knocked out";
  if (isMachine(t, "winch")) return "Right click to switch the winch on or off";
  if (t === T.SIGN) return "Right click to write on the sign";
  if (damaged && held === "patchKit") return "Right click to mend it with your patch kit";
  if (damaged) return "Cracked: a patch kit (right click) would mend it";
  return "";
}

/** Has the ship block (or piece) at `t` been cracked? */
function damagedAt(sim: Sim, t: EditTarget): boolean {
  const ship = t.kind === "ship" ? sim.ships.get(t.id) : undefined;
  if (!ship?.state.damage || !inGrid(ship.grid, t.x, t.y)) return false;
  const i = t.y * ship.grid.w + t.x;
  return ship.state.damage[pieceAnchor(ship.grid, i) ?? i] !== undefined;
}

/** Is the tile at `t` burning, or one right beside it (on a ship, or the ground of an asteroid)? */
function fireNear(sim: Sim, t: EditTarget, ground: Set<string>): boolean {
  const near = [[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1]] as const;
  if (t.kind === "asteroid") {
    const a = sim.asteroids.find((x) => x.def.id === t.id);
    return !!a && near.some(([dx, dy]) => ground.has(`${t.id}:${(t.y + dy) * a.grid.w + t.x + dx}`));
  }
  const ship = t.kind === "ship" ? sim.ships.get(t.id) : undefined;
  const fires = ship?.state.fires;
  if (!ship || !fires) return false;
  return near.some(([dx, dy]) => fires[(t.y + dy) * ship.grid.w + t.x + dx] !== undefined);
}
